import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { resolveBuildId } from "common-fe";
import { useTranslation } from "i18n-fe";
import { shouldAutoApplyUpdate } from "../util/pwaUpdatePolicy";
import { checkForUpdate } from "../util/swUpdateCheck";
import { applyWaitingUpdate } from "../util/applyWaitingUpdate";

// #3272 - the scribe's own "update now" from the account menu. The automatic
// path (ReloadPrompt.jsx / pwaUpdatePolicy.js) is untouched; this is a manual
// check that asks the browser for the newest sw.js immediately instead of
// waiting for it to notice. Because the scribe chose it, a reload on confirm
// is fine, but the same route safe-list still gates the button: mid-sheet the
// button is off and the dialog says to finish the sheet first (kiosk doctrine,
// a reload there drops uncommitted keystrokes).
//
// Mounted only while open (TopBar), so the check runs once per open and
// "Try again" re-runs it.
export default function CheckForUpdatesDialog({ onClose }) {
  const { t } = useTranslation("score");
  const location = useLocation();
  const currentBuildId = resolveBuildId();
  // { phase: "checking" | "upToDate" | "available" | "error", newBuildId }
  const [state, setState] = useState({ phase: "checking" });
  const [updating, setUpdating] = useState(false);
  const alive = useRef(true);

  const runCheck = useCallback(() => {
    setState({ phase: "checking" });
    checkForUpdate().then(
      (result) => {
        if (!alive.current) return;
        setState(
          result.status === "available"
            ? { phase: "available", newBuildId: result.newBuildId }
            : { phase: "upToDate" },
        );
      },
      () => {
        if (alive.current) setState({ phase: "error" });
      },
    );
  }, []);

  useEffect(() => {
    alive.current = true;
    runCheck();
    return () => {
      alive.current = false;
    };
  }, [runCheck]);

  const safeRoute = shouldAutoApplyUpdate(location.pathname);
  const { phase, newBuildId } = state;
  const vars = { current: currentBuildId, next: newBuildId };

  const applyUpdate = () => {
    setUpdating(true);
    // #3606: not vite-plugin-pwa's updateServiceWorker, which silently did
    // nothing here and left this dialog locked. See applyWaitingUpdate.js.
    applyWaitingUpdate();
  };

  let body;
  let actions;
  if (phase === "checking") {
    body = (
      <Stack direction="row" spacing={2} alignItems="center">
        <CircularProgress size={24} data-testid="update-checking-spinner" />
        <Typography>{t("pwa.updateDialog.checking")}</Typography>
      </Stack>
    );
    actions = <Button onClick={onClose}>{t("pwa.updateDialog.cancel")}</Button>;
  } else if (phase === "upToDate") {
    body = <Typography>{t("pwa.updateDialog.upToDate", vars)}</Typography>;
    actions = <Button onClick={onClose}>{t("pwa.updateDialog.close")}</Button>;
  } else if (phase === "available") {
    body = (
      <Stack spacing={2}>
        <Typography>
          {newBuildId
            ? t("pwa.updateDialog.available", vars)
            : t("pwa.updateDialog.availableUnknownVersion", vars)}
        </Typography>
        {!safeRoute && (
          <Typography color="warning.main" data-testid="update-finish-sheet">
            {t("pwa.updateDialog.finishSheetFirst")}
          </Typography>
        )}
        {updating && <Typography>{t("pwa.updateDialog.updating")}</Typography>}
      </Stack>
    );
    actions = (
      <>
        <Button onClick={onClose} disabled={updating}>
          {t("pwa.updateDialog.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={applyUpdate}
          disabled={!safeRoute || updating}
        >
          {t("pwa.updateDialog.updateNow")}
        </Button>
      </>
    );
  } else {
    body = <Typography>{t("pwa.updateDialog.couldNotCheck")}</Typography>;
    actions = (
      <>
        <Button onClick={onClose}>{t("pwa.updateDialog.close")}</Button>
        <Button variant="contained" onClick={runCheck}>
          {t("pwa.updateDialog.tryAgain")}
        </Button>
      </>
    );
  }

  return (
    <Dialog open onClose={updating ? undefined : onClose} data-testid="check-for-updates-dialog">
      <DialogTitle>{t("pwa.updateDialog.title")}</DialogTitle>
      <DialogContent>{body}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
}
